/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import styled from '@emotion/styled';
import {Container, media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {BlockIntro} from 'UI/components/layout/BlockIntro';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';
import {JobsCard} from 'UI/components/module/jobsBlock/JobsCard';
import {NewsCard} from 'UI/components/module/newsBlock/NewsCard';

import {isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';
import type {ListOverviewBlockType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the props used by the `ListOverviewBlock` component.
 * It includes essential metadata and content configuration necessary to render a timeline section with animated entries.
 * Each property reflects a specific feature of the timeline, including display content, structure, and test instrumentation.
 */
interface ComponentProps {
    id: ListOverviewBlockType['id'];
    intro: ListOverviewBlockType['intro'];
    listData?: PageData['documentsAndFolders'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'ListOverviewBlock'
     */
    testId?: string;
}

/**
 * The `ListOverviewBlock` component is a visual block used to render a chronological list of events with animations.
 * It wraps a sequence of timeline entries using the `TimelineItem` component and decorates them with a vertical line
 * that animates based on scroll position to indicate progression through the timeline.
 * Internally, this component uses `useListOverviewBlock` to handle references, animated progress state, and positioning of the line.
 * The component also ensures that the first and last items are tracked for calculating the scrollable animation range,
 * and applies alternating styling (even/odd) to distinguish items visually. It is reactive thanks to `mobx-react-lite`'s `observer`.
 *
 * @param props          The props object for ListOverviewBlock.
 * @param props.events   An array of event objects that each contain details like year, title, description, and image.
 * @param props.id       The unique ID for the block used for DOM identification and accessibility.
 * @param props.linkId   The ID used as an anchor target for in-page navigation.
 * @param props.overline A decorative text displayed above the title.
 * @param props.testId   A test identifier used for automated UI testing.
 * @param props.title    The main title of the timeline block section.
 * @returns A rendered section element containing timeline items and an animated progress line.
 *
 * @example
 * ```tsx
 * <ListOverviewBlock
 *   id="company-history"
 *   title="Our Journey"
 *   overline="Milestones"
 *   events={[
 *     { year: 2010, title: 'Founded', description: '...', image: ... },
 *     { year: 2015, title: 'Expansion', description: '...', image: ... }
 *   ]}
 * />
 * ```
 */
const ListOverviewBlock = observer(({id, intro, listData, testId = 'ListOverviewBlock'}: ComponentProps) => (
    <BlockWrapper>
        <Container>
            <BlockIntro blockId={id} intro={intro} testId={`${testId}-Intro`} />
            <ViewportTrigger baseComponent={Grid}>
                {listData?.map(item => {
                    if (isDefinedAndPopulated(item)) {
                        if (item.listView.type === 'Article') {
                            return (
                                <NewsCard
                                    key={item.id}
                                    date={item.updatedAt}
                                    headline={item.listView.headline}
                                    id={item.id}
                                    listDescription={item.listView.listDescription}
                                    listImage={item.listView.listImage}
                                    slug={item.slug}
                                    testId={`News-${item.id}`}
                                />
                            );
                        } else if (item.listView.type === 'JobPosting') {
                            return (
                                <JobsCard
                                    key={item.id}
                                    employmentType={item.listView.employmentType}
                                    headline={item.listView.headline}
                                    id={item.id}
                                    listDescription={item.listView.listDescription}
                                    slug={item.slug}
                                    testId={`Jobs-${item.id}`}
                                />
                            );
                        }
                    }

                    return null;
                })}
            </ViewportTrigger>
        </Container>
    </BlockWrapper>
));

ListOverviewBlock.displayName = 'ListOverviewBlock';

export {ListOverviewBlock};

const BlockWrapper = styled.div``;

const Grid = styled.div`
    column-gap: ${spacing(6)};
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto auto auto auto ${spacing(6)};

    ${media('sm')} {
        grid-template-columns: 1fr 1fr;
    }

    ${media('lg')} {
        grid-template-columns: 1fr 1fr 1fr;
    }
`;